import { useEffect, useRef, useState } from 'react';
import { pdfjs } from 'react-pdf';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import styled from 'styled-components';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
  overflow: auto;
`;

const PageContainer = styled.div`
  margin: 16px 0;
  position: relative;
  min-height: 800px;
  width: 100%;
  display: flex;
  justify-content: center;

  .react-pdf__Page {
    width: 100%;
    display: flex;
    justify-content: center;
  }
`;

const PDFViewer = ({ url }: { url: string }) => {
  const [numPages, setNumPages] = useState<number>();
  const [visiblePages, setVisiblePages] = useState<Set<number>>(new Set([1]));
  const observerRef = useRef<IntersectionObserver | null>(null);
  const pageRefs = useRef<(HTMLDivElement | null)[]>([]);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }): void => {
    setNumPages(numPages);
    pageRefs.current = new Array(numPages).fill(null);
  };

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const pageNumber = parseInt(
            entry.target.getAttribute('data-page') || '0',
            10,
          );
          if (entry.isIntersecting) {
            setVisiblePages((prev) => new Set([...prev, pageNumber]));
          } else {
            setVisiblePages((prev) => {
              const newSet = new Set(prev);
              if (Math.abs(pageNumber - Array.from(prev)[0]) > 5) {
                newSet.delete(pageNumber);
              }
              return newSet;
            });
          }
        });
      },
      {
        rootMargin: '400px 0px',
        threshold: 0.1,
      },
    );

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    if (observerRef.current) {
      pageRefs.current.forEach((ref) => {
        if (ref) {
          observerRef.current?.observe(ref);
        }
      });
    }
  }, [numPages]);

  return (
    <Container>
      <Document file={url} onLoadSuccess={onDocumentLoadSuccess}>
        {Array.from(new Array(numPages), (_, index) => (
          <PageContainer
            key={`page_${index + 1}`}
            data-page={index + 1}
            ref={(el: HTMLDivElement | null) => (pageRefs.current[index] = el)}
          >
            {visiblePages.has(index + 1) && <Page pageNumber={index + 1} />}
          </PageContainer>
        ))}
      </Document>
    </Container>
  );
};

export default PDFViewer;
